/**
 * The flying monsters and the ghost (05 §6.1) in their arcade look: Monsta, Pulpul, the Invader
 * (who walks, but is built like the flyers), and Skel-monsta (Baron von Blubba), who comes for
 * dawdlers after HURRY UP. Facing +x like every figure.
 */

import { type Material, Matrix4 } from 'three';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import type { Palette } from '../palette';
import { slit } from './monstersWalk';
import { bodyRamp, eye } from './parts';
import { Puppet } from './puppet';

/** Monsta: a purple whale-ghost with a gaping jaw and one red eye; drifts at 45° and bounces. */
export function buildMonsta(p: Palette, angry: boolean, material: Material): Puppet {
  const r = bodyRamp('monsta', angry);
  const pup = new Puppet(material);
  pup.turn = 0.6;
  const body = new MeshBuilder();
  body.blob(0.8, 0.52, 0.56, p.color(`${r}.2`), place(0, 0.12, 0), { detail: 3 });
  // The lower jaw, hanging open under the snout.
  body.blob(0.5, 0.16, 0.44, p.color(`${r}.1`), new Matrix4().makeTranslation(0.24, -0.36, 0)
    .multiply(new Matrix4().makeRotationZ(-0.25)), { detail: 2 });
  body.blob(0.3, 0.12, 0.34, p.color('ink.1'), place(0.36, -0.22, 0), { detail: 2 });
  // A flat tail fin behind.
  body.blob(0.3, 0.14, 0.28, p.color(`${r}.1`), new Matrix4().makeTranslation(-0.86, 0.26, 0)
    .multiply(new Matrix4().makeRotationZ(0.6)), { detail: 2 });
  body.blob(0.14, 0.16, 0.08, p.color('cheek.1'), place(0.36, 0.26, 0.46), { detail: 2 });
  body.blob(0.05, 0.1, 0.04, p.color('paper.0'), place(0.36, 0.26, 0.52), { detail: 1 });
  body.blob(0.22, 0.06, 0.1, p.color(`${r}.3`), place(-0.16, 0.56, 0.24), { detail: 1 });
  pup.add('body', body, 0, 0.9, 0);
  return pup;
}

/** Pulpul: a salmon-pink puff with a purple top-knot and little yellow hands and feet. */
export function buildPulpul(p: Palette, angry: boolean, material: Material): Puppet {
  const r = bodyRamp('pulpul', angry);
  const pup = new Puppet(material);
  pup.turn = 0.9;
  const body = new MeshBuilder();
  body.blob(0.62, 0.6, 0.58, p.color(`${r}.2`), place(0, 0, 0), { detail: 3 });
  slit(body, p, 0.6, 0.06, 0.13, 0.17);
  slit(body, p, 0.6, 0.06, -0.13, 0.17);
  for (const [x, y, z] of [[0.2, 0.2, 0.62], [0.2, 0.2, -0.62], [0.25, -0.52, 0.34], [0.25, -0.52, -0.34]] as const) {
    body.blob(0.14, 0.12, 0.12, p.color('bubTrim.1'), place(x, y, z), { detail: 2 });
  }
  pup.add('body', body, 0, 0.8, 0);
  const knot = new MeshBuilder();
  knot.blob(0.14, 0.12, 0.3, p.color('monsta.2'), place(0, 0, 0.14), { detail: 2 });
  knot.blob(0.14, 0.12, 0.3, p.color('monsta.2'), place(0, 0, -0.14), { detail: 2 });
  knot.blob(0.1, 0.1, 0.1, p.color('monsta.1'), place(0, 0, 0), { detail: 1 });
  pup.add('prop', knot, 0, 0.62, 0, 'body');
  return pup;
}

/** Invader: a lavender onion of an alien with cyan antennae, slit eyes and pink feet; drops lasers. */
export function buildInvader(p: Palette, angry: boolean, material: Material): Puppet {
  const r = bodyRamp('invader', angry);
  const pup = new Puppet(material);
  pup.turn = 1.1;
  const body = new MeshBuilder();
  body.blob(0.6, 0.5, 0.6, p.color(`${r}.2`), place(0, 0.56, 0), { detail: 3 });
  body.cylinder(0.34, 0, 0.62, 16, p.color(`${r}.2`), place(0, 0.9, 0));
  body.blob(0.16, 0.12, 0.16, p.color(`${r}.1`), place(0, 0.32, 0.58), { detail: 1 });
  body.blob(0.16, 0.12, 0.16, p.color(`${r}.1`), place(0, 0.32, -0.58), { detail: 1 });
  slit(body, p, 0.59, 0.62, 0.14, 0.18);
  slit(body, p, 0.59, 0.62, -0.14, 0.18);
  for (const z of [0.26, -0.26]) {
    body.cylinder(0.025, 0.025, 0.3, 5, p.color('antenna.1'), new Matrix4().makeTranslation(0, 1.2, z)
      .multiply(new Matrix4().makeRotationX(z > 0 ? 0.5 : -0.5)));
    body.blob(0.06, 0.06, 0.06, p.color('antenna.2'), place(0, 1.46, z * 1.5), { detail: 1 });
  }
  pup.add('body', body, 0, 0, 0);
  for (const [name, z] of [['legA', 0.3], ['legB', -0.3]] as const) {
    const leg = new MeshBuilder();
    leg.blob(0.2, 0.1, 0.14, p.color('hotFeet.1'), place(0.04, 0, 0), { flatBottom: true, detail: 2 });
    pup.add(name, leg, 0, 0.08, z, 'body');
  }
  return pup;
}

/** Skel-monsta (Baron von Blubba): a pale whale-ghost with a skull's face. Larger than anything. */
export function buildSkelMonsta(p: Palette, material: Material): Puppet {
  const pup = new Puppet(material);
  pup.turn = 0.6;
  const body = new MeshBuilder();
  body.blob(1.15, 0.9, 0.82, p.color('skel.2'), place(0, 0, 0), { detail: 3 });
  body.cylinder(0.4, 0.05, 0.8, 12, p.color('skel.1'), new Matrix4().makeTranslation(-1.0, 0.1, 0)
    .multiply(new Matrix4().makeRotationZ(1.9)));
  body.blob(0.3, 0.34, 0.28, p.color('ink.0'), place(0.7, 0.22, 0.34), { detail: 2 });
  body.blob(0.3, 0.34, 0.28, p.color('ink.0'), place(0.7, 0.22, -0.34), { detail: 2 });
  eye(body, p, 0.9, 0.2, 0.34, 0.16, p.color('fire.2'));
  eye(body, p, 0.9, 0.2, -0.34, 0.16, p.color('fire.2'));
  body.box(0.2, 0.2, 0.9, p.color('ink.0'), place(0.95, -0.48, 0));
  for (let i = 0; i < 4; i++) body.box(0.14, 0.14, 0.14, p.color('skel.3'), place(1.02, -0.36, -0.3 + i * 0.2));
  pup.add('body', body, 0, 1.1, 0);
  return pup;
}
